import React, { useEffect, useMemo, useRef, useCallback } from 'react';
import { useForm } from '@mantine/form';
import { Paper, Button, Stack, Divider, LoadingOverlay, Alert, Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconDeviceFloppy, IconAlertCircle, IconCheck } from '@tabler/icons-react';
import { useParams, useNavigate } from 'react-router-dom';
import { BasicInfo } from './BasicInfo';
import { Location } from './Location';
import { Measures } from './Measures';
import { AuditFormData, IstMassnahme } from './types';
import { useFormReducer } from '../../hooks/useFormReducer';
import { useRetryOperation } from '../../hooks/useRetryOperation';
import { MeasureService } from '../../services/MeasureService';
import { 
  pb,
  createAudit,
  updateAudit,
  fetchAuditoren,
  fetchDirektionen,
  fetchKontrollpunkte,
  type Audit
} from '../../lib/pocketbase';

const defaultMassnahme: IstMassnahme = {
  id: '',
  reihenfolge: 1,
  istzustand: '',
  massnahme: '',
  bemerkung: '',
  bereich: '',
  erledigt: false,
  anhaenge: [],
  existingAnhaenge: []
};

const defaultFormValues: AuditFormData = {
  auditDatum: new Date(),
  auditor: [],
  begleitpersonen: '',
  bemerkungen: '',
  dienststelle: '',
  adresse: '',
  kontrollpunkt: [],
  measures: [{ ...defaultMassnahme }]
};

export function AuditForm() {
  const { auditnummer } = useParams();
  const navigate = useNavigate();
  const retry = useRetryOperation();
  const { 
    state: formState, 
    setLoading, 
    setSubmitting, 
    setError, 
    setData,
    setAuditId
  } = useFormReducer();

  const isNewAudit = useMemo(() => !auditnummer || auditnummer === 'new', [auditnummer]);
  const initialDataLoaded = useRef(false);
  const auditDataLoaded = useRef(false);

  const form = useForm<AuditFormData>({
    initialValues: defaultFormValues,
    validateInputOnBlur: true,
    validate: {
      auditDatum: (value) => (!value ? 'Datum ist erforderlich' : null),
      auditor: (value) => (!value || value.length === 0 ? 'Auditor ist erforderlich' : null),
      dienststelle: () => null,
      adresse: () => null,
      kontrollpunkt: () => null,
      measures: {
        istzustand: (value) => {
          if (!value || value.length < 10) {
            return 'Ist-Zustand muss mindestens 10 Zeichen lang sein';
          }
          if (value.length > 1000) {
            return 'Ist-Zustand darf maximal 1000 Zeichen lang sein';
          }
          return null;
        },
        massnahme: (value) => {
          if (!value || value.length < 10) {
            return 'Massnahme muss mindestens 10 Zeichen lang sein';
          }
          if (value.length > 1000) {
            return 'Massnahme darf maximal 1000 Zeichen lang sein';
          }
          return null;
        },
        bereich: (value) => (!value ? 'Thema ist erforderlich' : null),
      },
    },
  });

  const loadInitialData = useCallback(async () => {
    if (initialDataLoaded.current) return;
    initialDataLoaded.current = true;
    
    try {
      setLoading(true);
      setError(null);

      const [auditoren, direktionen, kontrollpunkte] = await Promise.all([
        retry(() => fetchAuditoren()),
        retry(() => fetchDirektionen()),
        retry(() => fetchKontrollpunkte())
      ]);

      setData({
        auditoren,
        direktionen,
        kontrollpunkte,
        isLoading: false
      });

      if (isNewAudit) {
        // Instead of form.reset(), set the values explicitly to ensure arrays are preserved
        form.setValues({
          ...defaultFormValues,
          auditor: [], // Explicitly set as array
          kontrollpunkt: [], // Explicitly set as array
          measures: [{ ...defaultMassnahme }]
        });
      }
    } catch (error: any) {
      setError('Fehler beim Laden der Formulardaten: ' + error.message);
      setLoading(false);
      notifications.show({
        title: 'Fehler',
        message: 'Formulardaten konnten nicht geladen werden',
        color: 'red',
        icon: <IconAlertCircle size={16} />
      });
    }
  }, [form, isNewAudit, retry, setData, setError, setLoading]);

  const loadAuditData = useCallback(async () => {
    if (isNewAudit || formState.auditId || auditDataLoaded.current) return;
    auditDataLoaded.current = true;

    try {
      setLoading(true);
      setError(null);
      
      const records = await retry(() =>
        pb.collection('audits').getList<Audit>(1, 1, {
          filter: `auditnummer = "${auditnummer}"`,
          expand: 'dienststelle.direktion,bereich,auditor',
          fields: `
            id,
            auditnummer,
            auditdatum,
            auditor,
            begleitpersonen,
            bemerkungen,
            dienststelle,
            bereich,
            kontrollpunkt,
            expand.dienststelle.id,
            expand.dienststelle.name,
            expand.dienststelle.direktion,
            expand.dienststelle.expand.direktion.id,
            expand.dienststelle.expand.direktion.name,
            expand.bereich.id,
            expand.bereich.name,
            expand.bereich.adresse,
            expand.auditor.id,
            expand.auditor.vorname,
            expand.auditor.nachname,
            expand.auditor.avatar
          `
        })
      );

      if (records.items.length === 0) throw new Error('Audit nicht gefunden');

      const record = records.items[0];
      const massnahmen = await MeasureService.getMeasuresForAudit(record.id);

      const direktionId = record.expand?.dienststelle?.direktion || null;
      const dienststelleId = record.dienststelle || '';

      const loadedValues = {
        auditDatum: new Date(record.auditdatum),
        auditor: record.auditor || [],
        begleitpersonen: record.begleitpersonen || '',
        bemerkungen: record.bemerkungen || '',
        direktion: direktionId,
        dienststelle: dienststelleId,
        bereich: record.bereich || null,
        adresse: record.expand?.bereich?.adresse || '',
        kontrollpunkt: Array.isArray(record.kontrollpunkt) ? record.kontrollpunkt : [record.kontrollpunkt],
        measures: massnahmen.length > 0 ? massnahmen : [{ ...defaultMassnahme }]
      } satisfies AuditFormData;

      console.log('[AuditForm] Loaded audit data', {
        auditnummer: record.auditnummer,
        direktion: direktionId,
        dienststelle: dienststelleId,
        bereich: loadedValues.bereich
      });

      form.setValues(loadedValues);

      setAuditId(record.id);
    } catch (error: any) {
      setError('Fehler beim Laden des Audits: ' + error.message);
      setLoading(false);
      notifications.show({
        title: 'Fehler',
        message: 'Audit konnte nicht geladen werden',
        color: 'red',
        icon: <IconAlertCircle size={16} />
      });
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  }, [auditnummer, isNewAudit, formState.auditId, form, retry, setAuditId, setError, setLoading, navigate]);

  useEffect(() => {
    loadInitialData();
  }, [loadInitialData]);

  useEffect(() => {
    loadAuditData();
  }, [loadAuditData]);

  const handleSubmit = async (values: AuditFormData) => {
    try {
      setSubmitting(true);
      setError(null);

      // Validate measures
      const measureErrors = values.measures.map((measure, index) => {
        const errors: string[] = [];
        if (!measure.istzustand || measure.istzustand.length < 10) {
          errors.push(`Massnahme ${index + 1}: Ist-Zustand muss mindestens 10 Zeichen lang sein`);
        }
        if (measure.istzustand && measure.istzustand.length > 1000) {
          errors.push(`Massnahme ${index + 1}: Ist-Zustand darf maximal 1000 Zeichen lang sein`);
        }
        if (!measure.massnahme || measure.massnahme.length < 10) {
          errors.push(`Massnahme ${index + 1}: Massnahme muss mindestens 10 Zeichen lang sein`);
        }
        if (measure.massnahme && measure.massnahme.length > 1000) {
          errors.push(`Massnahme ${index + 1}: Massnahme darf maximal 1000 Zeichen lang sein`);
        }
        if (!measure.bereich) {
          errors.push(`Massnahme ${index + 1}: Thema ist erforderlich`);
        }
        return errors;
      });

      const allErrors = measureErrors.flat();
      if (allErrors.length > 0) {
        notifications.show({
          title: 'Validierungsfehler',
          message: (
            <Stack gap="xs">
              {allErrors.map((error, index) => (
                <Text key={index} size="sm">{error}</Text>
              ))}
            </Stack>
          ),
          color: 'red',
          icon: <IconAlertCircle size={16} />
        });
        setError('Bitte korrigieren Sie die Fehler in den Massnahmen');
        setSubmitting(false);
        return;
      }

      // Ensure auditDatum is a Date object
      const auditDate = values.auditDatum instanceof Date
        ? values.auditDatum
        : new Date(values.auditDatum);
      
      const auditData = {
        auditdatum: auditDate.toISOString().split('T')[0],
        auditor: values.auditor,
        begleitpersonen: values.begleitpersonen,
        bemerkungen: values.bemerkungen,
        direktion: values.direktion || null,
        dienststelle: values.dienststelle,
        bereich: values.bereich || null,
        kontrollpunkt: values.kontrollpunkt,
        status: isNewAudit ? 'Geplant' : 'In Bearbeitung',
      };

      const audit = isNewAudit
        ? await retry(() => createAudit(auditData))
        : await retry(() => updateAudit(formState.auditId!, auditData));

      // Process measures sequentially to avoid race conditions
      for (let i = 0; i < values.measures.length; i++) {
        const measure = values.measures[i];
        const measureData = {
          ...measure,
          audit: audit.id,
          reihenfolge: i + 1,
          nummer: measure.nummer || i + 1
        };

        try {
          if (measure.id) {
            await MeasureService.updateMeasure(measure.id, measureData);
          } else {
            await MeasureService.createMeasure(measureData);
          }
        } catch (error: any) {
          console.error(`Error processing measure ${i + 1}:`, error);
          notifications.show({
            title: 'Fehler',
            message: `Fehler beim Speichern von Massnahme ${i + 1}: ${error.message}`,
            color: 'red',
            icon: <IconAlertCircle size={16} />
          });
          throw error;
        }
      }

      notifications.show({
        title: 'Erfolg',
        message: isNewAudit ? 'Audit wurde erstellt' : 'Audit wurde aktualisiert',
        color: 'green',
        icon: <IconCheck size={16} />
      });

      navigate('/dashboard');
    } catch (error: any) {
      const errorMessage = 'Fehler beim Speichern: ' + error.message;
      setError(errorMessage);
      notifications.show({
        title: 'Fehler',
        message: errorMessage,
        color: 'red',
        icon: <IconAlertCircle size={16} />
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (formState.isLoading && !initialDataLoaded.current) {
    return <LoadingOverlay visible />;
  }

  return (
    <form onSubmit={form.onSubmit(handleSubmit)} noValidate>
      <Stack gap="xl">
        {formState.error && (
          <Alert icon={<IconAlertCircle size={16} />} color="red" title="Fehler">
            {formState.error}
          </Alert>
        )}

        <Paper shadow="xs" p="md" withBorder>
          <BasicInfo
            form={form}
            auditoren={formState.auditoren}
            direktionen={formState.direktionen}
            dienststellen={formState.dienststellen}
            auditnummer={auditnummer}
          />
        </Paper>

        <Divider />

        <Paper shadow="xs" p="md" withBorder>
          <Location
            form={form}
            kontrollpunkte={formState.kontrollpunkte}
          />
        </Paper>

        <Divider />

        <Paper shadow="xs" p="md" withBorder>
          <Measures 
            measures={form.values.measures}
            setMeasures={(measures) => form.setFieldValue('measures', measures)}
            defaultMassnahme={defaultMassnahme}
          />
        </Paper>

        <Button
          type="submit"
          leftSection={<IconDeviceFloppy size={16} />}
          size="md"
          loading={formState.isSubmitting}
        >
          {isNewAudit ? 'Speichern' : 'Aktualisieren'}
        </Button>
      </Stack>
    </form>
  );
}
